<template>
  <!-- Renders nothing until a digest exists (HTTP 204 -> null), so the coach
       stays invisible for companies that aren't coach_enabled or whose nightly
       run hasn't produced one yet. The presence of a cached digest is the gate. -->
  <!-- BARE: just the prose — used where Allen is already introduced on the surface
       (e.g. the Today briefing), so no avatar / bubble / name is repeated. -->
  <div v-if="bare && digest && digest.digest" class="allen-body allen-body--bare" v-html="bodyHtml"></div>

  <div v-else-if="digest && digest.digest" class="allen-card">
    <div class="allen-avatar" aria-hidden="true">
      <i class="bi bi-robot"></i>
    </div>
    <div class="allen-bubble">
      <div class="allen-head">
        <span class="allen-name">{{ heading || 'Allen' }}</span>
        <span v-if="!heading" class="allen-role">{{ $t('today.digest.role') }}</span>
        <span v-if="generatedLabel" class="allen-date">{{ generatedLabel }}</span>
      </div>
      <!-- digest.digest is a grounded HTML fragment from the local narrator;
           every number was validated against the signal before it was cached. -->
      <div class="allen-body" v-html="bodyHtml"></div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCompanyStore } from '@/stores/CompanyStore'
import { formatDate } from '@/i18n/format'

const props = defineProps({
  digestSlot: { type: String, default: 'past_30_days' },
  // Demo pass-through: a frozen digest payload. When set, render it and make
  // NO API call (the demo store is unauthenticated + hard-coded).
  staticDigest: { type: Object, default: null },
  // When set, the head shows this title (e.g. 'Store Performance Digest') instead
  // of the 'Allen · your store companion' identity — used where Allen is already
  // introduced elsewhere on the page, so we don't name him a third time.
  heading: { type: String, default: '' },
  // Render just the prose (no avatar/bubble/name) — for surfaces that already
  // introduce Allen once (the Today briefing).
  bare: { type: Boolean, default: false },
  // Digest variant to fetch when live (no staticDigest): 'synthesis' for the
  // Today briefing (hero + gauges + focus cards carry the detail, so the digest
  // is trimmed to the goal-judgment + one watch-out); '' / 'full' for the
  // self-contained standalone digest (HomeOverview). Backend falls back to full.
  // NB: named 'variant', not 'style' — 'style' collides with Vue's reserved
  // inline-style attribute and would never reach the component as a prop.
  variant: { type: String, default: '' },
})

const companyStore = useCompanyStore()
const { locale } = useI18n()
const digest = computed(() => props.staticDigest || companyStore.digest)

// When we render our own heading, drop a leading <h3> the narrator emitted so the
// title doesn't show twice (the narrator is prompted to open with an <h3>).
const bodyHtml = computed(() => {
  const html = digest.value?.digest || ''
  return (props.heading || props.bare) ? html.replace(/^\s*<h3[^>]*>[\s\S]*?<\/h3>\s*/i, '') : html
})

const generatedLabel = computed(() => {
  const ts = digest.value?.generated_at
  if (!ts) return ''
  try {
    // English: the browser's date, as before. Dutch: dd/mm/yyyy.
    if (locale.value === 'nl') return formatDate(new Date(ts), { style: 'numeric', lang: 'nl' })
    return new Date(ts).toLocaleDateString()
  } catch (_) {
    return ''
  }
})

onMounted(() => {
  if (!props.staticDigest) companyStore.fetchDigest(props.digestSlot, { style: props.variant })
})
</script>

<style scoped>
.allen-card {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
}

/* Avatar — the "your coach is talking" identity cue, in the brand palette */
.allen-avatar {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff;
  font-size: 1.4rem;
  box-shadow: 0 4px 10px rgba(79, 126, 179, 0.35);
}

/* Speech bubble — same shell as a platform .panel, with a tail to the avatar */
.allen-bubble {
  position: relative;
  flex: 1 1 auto;
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  padding: 1rem 1.25rem 1.1rem;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.06);
}
.allen-bubble::before {
  content: '';
  position: absolute;
  top: 16px;
  left: -8px;
  width: 14px;
  height: 14px;
  background: var(--stora-card, #fff);
  border-left: 1px solid var(--stora-border, #e6edf3);
  border-bottom: 1px solid var(--stora-border, #e6edf3);
  transform: rotate(45deg);
}

.allen-head {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}
.allen-name { font-weight: 700; color: var(--stora-accent, #4F7EB3); }
.allen-role { font-size: 0.8rem; color: var(--stora-muted, #6b7a86); }
.allen-date { margin-left: auto; font-size: 0.78rem; color: #9aa7b1; }

.allen-body :deep(h3) { font-size: 1.05rem; margin: 0.1rem 0 0.55rem; color: #1d2b3a; font-weight: 700; }
.allen-body :deep(p) { margin: 0.25rem 0 0.65rem; line-height: 1.6; color: #3a4754; }
.allen-body :deep(ul) { margin: 0; padding-left: 1.15rem; }
.allen-body :deep(li) { margin: 0.4rem 0; line-height: 1.55; color: #3a4754; }
.allen-body :deep(strong) { color: #1d2b3a; }
</style>
